/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Page from 'components/custom/template/Page';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faIdBadge,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import { Icon } from '@iconify/react';
import Avatar from 'components/base/Avatar';
import { formatDate } from 'services/utils';

// Mock data for service history
const mockServiceHistory = [
  {
    id: 1,
    user_id: 101,
    user_img_upload: '/path/to/image1.jpg',
    user_name: 'John Doe',
    mobile: '9877778754',
    employee_unique_id: 'EMP-2020-001',
    designation_name: 'Junior Developer',
    department: 'Engineering',
    join_date: '2020-01-15',
    end_date: '2021-06-30',
    duration: '1 year, 5 months',
    resign_reason: 'Career Growth Opportunity'
  },
  {
    id: 2,
    user_id: 101,
    user_img_upload: '/path/to/image1.jpg',
    user_name: 'John Doe',
    mobile: '9567778754',
    employee_unique_id: 'EMP-2022-001',
    designation_name: 'Senior Developer',
    department: 'Engineering',
    join_date: '2022-03-10',
    end_date: null, // Currently working
    duration: '2 years, 2 months',
    resign_reason: null
  }
];

// Column definitions for service history table
const ServiceHistoryColumns = () => [
  {
    accessorKey: 'user_name',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-primary-100 rounded me-2">
            <FeatherIcon
              icon="user"
              size={16}
              className="text-primary-600 dark__text-primary-300"
            />
          </div>
          <span>Employee</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const {
        user_id,
        user_img_upload,
        user_name,
        mobile,
        designation_name,
        employee_unique_id
      } = original;

      return (
        <div className="">
          <Link to={`/employee/detail/${user_id}`} className="fs-8 fw-bold">
            {user_name}
          </Link>
          <div className="d-flex align-items-center">
            <Link
              className="mb-0 text-1000 fw-semi-bold fs-9 me-2"
              to={`tel:${mobile}`}
            >
              {mobile || '--'}
            </Link>
          </div>
          {employee_unique_id && (
            <span className="fs-9 text-700">ID: {employee_unique_id}</span>
          )}
          {/* </div> */}
        </div>
      );
    },
    meta: {
      headerProps: {
        className: 'ps-0 border-end me-1 bg-white'
      },
      cellProps: {
        className: 'white-space-nowrap ps-0 border-end me-1 bg-white'
      }
    }
  },
  {
    accessorKey: 'designation_name',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-success-100 rounded me-2">
            <FeatherIcon
              icon="briefcase"
              size={16}
              className="text-success-600 dark__text-success-300"
            />
          </div>
          <span>Position</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { designation_name, department } = original;

      return (
        <div>
          <div className="fw-medium text-1000">{designation_name}</div>
          <p className="fs-8 me-2 mb-0 pe-lg-2 text-700">{department}</p>
        </div>
      );
    },
    meta: {
      headerProps: {
        className: 'ps-3 pe-3'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-3'
      }
    }
  },
  {
    accessorKey: 'join_date',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-success-100 rounded me-2">
            <Icon
              icon="codicon:sign-in"
              fontSize={16}
              className="text-success-600 dark__text-success-300"
            />
          </div>
          <span>Join Date</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { join_date } = original;
      const formattedDate = join_date
        ? formatDate(join_date, 'MMM DD, YYYY')
        : '--';

      return <div className="text-success fw-medium">{formattedDate}</div>;
    },
    meta: {
      headerProps: {
        className: 'ps-3 pe-3'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-3'
      }
    }
  },
  {
    accessorKey: 'end_date',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-danger-100 rounded me-2">
            <Icon
              icon="codicon:sign-out"
              fontSize={16}
              className="text-danger-600 dark__text-danger-300"
            />
          </div>
          <span>End Date</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { end_date } = original;
      const formattedDate = end_date
        ? formatDate(end_date, 'MMM DD, YYYY')
        : 'Present';

      return <div className="text-danger fw-medium">{formattedDate}</div>;
    },
    meta: {
      headerProps: {
        className: 'ps-3 pe-3'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-3'
      }
    }
  },
  {
    accessorKey: 'duration',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-info-100 rounded me-2">
            <FeatherIcon
              icon="clock"
              size={16}
              className="text-info-600 dark__text-info-300"
            />
          </div>
          <span>Duration</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { duration } = original;

      return <div className="fw-medium">{duration}</div>;
    },
    meta: {
      headerProps: {
        className: 'ps-3 pe-3'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-3'
      }
    }
  },
  {
    accessorKey: 'resign_reason',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-info-100 rounded me-2">
            <FontAwesomeIcon
              icon={faIdBadge}
              size={16}
              className="text-info-600 dark__text-info-300"
            />
          </div>
          <span>Reason</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { resign_reason, status } = original;

      return (
        <div className="fs-9 fw-medium">
          {status === 'Active' ? 'N/A' : resign_reason || '--'}
        </div>
      );
    },
    meta: {
      headerProps: {
        className: 'ps-3 pe-3'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-3'
      }
    }
  }
];

export default function EmployeeServiceHistory() {
  const [serviceHistory] = useState(mockServiceHistory);
  const refreshDataRef = useRef(null);

  // Functions for handling refresh
  const handleRefreshDataRegister = refreshData => {
    refreshDataRef.current = refreshData;
  };

  const handleRefresh = () => {
    if (refreshDataRef.current) {
      refreshDataRef.current();
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h2 className="mb-0">
          <FontAwesomeIcon icon={faHistory} className="me-3" />
          Employee Service History
        </h2>
      </div>
      <div className="p-4">
        <Page
          tableColumn={{
            column: ServiceHistoryColumns()
          }}
          dummyData={serviceHistory}
          exportName="employee_service_history"
          onRegisterRefresh={handleRefreshDataRegister}
        />
      </div>
    </>
  );
}
